//@ts-check
/**
 * A person's lists — private or sharing tags over their things (guides/DYNAMODB.md) — and which
 * things are on each.
 *
 * Every change is optimistic, like the things store: the screen changes at once and the request
 * catches up, and a refused request puts it back and says so. Lists are loaded with the things
 * (`loadThings`), so every refresh of one is a refresh of both.
 */
import ky from "ky";
import { ulid } from "ulid";
import { getfetchopts } from "$lib/utils/utils.js";
import { showError } from "$lib/notice.svelte.js";
import { getLocaleTag, getTranslations } from "$lib/utils/locale.svelte.js";
import messages from "$lib/messages/things.js";
import { apiBase, appHost } from "$lib/env.js";
import { failure } from "$lib/me.js";
import { limitReached } from "$lib/limits.js";

// Relative on the web; the app host inside the iOS app ($lib/env.js).
const basePath = apiBase();

/** Under the things API's mapping, so no new route through CloudFront (guides/ARCH.md). */
const LISTS_URL = `${basePath}/api/things/lists`;

/**
 * @typedef {object} List
 * @property {string} listId
 * @property {string} name      the owner's own words, emoji included — content, never translated
 * @property {boolean} sharing  a sharing list makes its things visible to its viewers
 * @property {string | null} date  something to look forward to, YYYY-MM-DD, or none
 * @property {boolean} keepOutOfAll  private lists only: its things are left out of All, and
 *   appear only when a list they are on is chosen — a place to park things without deleting them
 * @property {import("$lib/people.svelte.js").Person[]} viewers  the people it is shared with, by
 *   name and handle; a private list keeps the ones it had, who see it again if it is shared again
 * @property {string | null} link  a sharing list's link for people without the app, its token, or
 *   none (wip-archive/social/SOCIAL_PHASE3B_THE_LINK.md)
 */

/** @type {{ items: List[], loaded: boolean }} */
export const lists = $state({ items: [], loaded: false });

/**
 * The ids of the things on each list, by list id.
 *
 * @type {Record<string, string[]>}
 */
const members = $state({});

/**
 * Filings made on screen for captures the server has not got yet, by list id. A capture's lists
 * travel with it, so until it lands the server does not know them — and a refresh that replaced
 * the filings wholesale would take a thing just captured into Christmas off Christmas. Kept until
 * the server reports the filing itself, as the things store keeps a capture that is still sending.
 *
 * @type {Record<string, string[]>}
 */
const unsent = {};

/**
 * Which list My Things is showing — a list's id, or "" for All.
 *
 * Kept here rather than in the page so it outlives the page: check Christmas in a shop, glance at
 * Home, come back, and it is still Christmas. It lasts while the app is open; opening the app
 * afresh starts on All. Tapping My Things in the nav while already at the top returns to All.
 *
 * @type {{ selected: string }}
 */
export const listView = $state({ selected: "" });

/**
 * The list sheet: making a list (`listId` empty) or changing one — or, with `offer`, offering the
 * link of a list just made a sharing one.
 *
 * @type {{ open: boolean, listId: string, offer: boolean }}
 */
export const listSheet = $state({ open: false, listId: "", offer: false });

/** @param {string} [listId] a list to change; none to make a new one */
export function openListSheet(listId = "") {
  listSheet.listId = listId;
  listSheet.offer = false;
  listSheet.open = true;
}

/**
 * The sheet on a list just made a sharing one, offering its link to family without the app
 * (wip-archive/social/SOCIAL_PHASE3B_THE_LINK.md, "Found, not hunted for").
 *
 * @param {string} listId
 */
export function offerListLink(listId) {
  listSheet.listId = listId;
  listSheet.offer = true;
  listSheet.open = true;
}

export function closeListSheet() {
  listSheet.open = false;
  listSheet.listId = "";
  listSheet.offer = false;
}

/**
 * A message in the current locale, looked up when it is shown — as the things store does.
 *
 * @param {string} key
 * @param {Record<string, any>} [params]
 */
const t = (key, params) => getTranslations(messages)(key, params);

/**
 * A list as the server sends it, without the ids of its things — those are kept in `members`.
 *
 * @param {any} list
 * @returns {List}
 */
function listOnly(list) {
  const copy = { ...list };
  delete copy.thingIds;
  return copy;
}

/** Load the lists and what is on them. Whatever is on screen stays while the request runs. */
export async function loadLists() {
  try {
    const fetchopts = await getfetchopts();
    const res = await ky.get(LISTS_URL, fetchopts).json();
    const loaded = res.lists || [];
    lists.items = loaded.map((list) => ({ viewers: [], ...listOnly(list) }));
    for (const key of Object.keys(members)) delete members[key];
    for (const list of loaded) {
      const onServer = list.thingIds || [];
      unsent[list.listId] = (unsent[list.listId] || []).filter((id) => !onServer.includes(id));
      members[list.listId] = [...onServer, ...unsent[list.listId]];
    }
    lists.loaded = true;
    // A list removed on another device takes the view back to All rather than to an empty screen.
    if (listView.selected && !lists.items.some((list) => list.listId === listView.selected)) listView.selected = "";
  } catch (err) {
    console.error("Failed to load lists", err);
  }
}

/**
 * Whether a thing is on a list.
 *
 * @param {List} list
 * @param {{ thingId: string }} thing
 */
export function isOnList(list, thing) {
  return members[list.listId]?.includes(thing.thingId) ?? false;
}

/**
 * Whether a thing belongs in All: it does unless it is on a list kept out of All. Being on an
 * everyday list as well does not bring it back — parking it is the stronger statement.
 *
 * @param {{ thingId: string }} thing
 */
export function isInAll(thing) {
  return !lists.items.some((list) => list.keepOutOfAll && !list.sharing && isOnList(list, thing));
}

/**
 * Make a list. Its id is minted here, so its chip appears before the answer does and a retried
 * request lands on the same list.
 *
 * @param {{ name: string, sharing: boolean, date: string | null, keepOutOfAll: boolean }} fields
 * @returns {Promise<List | null>} the list, or null if it was refused
 */
export async function createList(fields) {
  const made = { listId: ulid(), ...fields, keepOutOfAll: fields.sharing ? false : fields.keepOutOfAll };
  // A list is made with nobody on it; the people picked while making it are added once it is.
  /** @type {List} */
  const list = { ...made, viewers: [] };
  lists.items = [...lists.items, list];
  members[list.listId] = [];
  try {
    const fetchopts = await getfetchopts({ json: made });
    const res = await ky.post(LISTS_URL, fetchopts).json();
    const saved = { ...listOnly(res.list), viewers: [] };
    lists.items = lists.items.map((l) => (l.listId === list.listId ? saved : l));
    return saved;
  } catch (err) {
    console.error("Failed to create list", err);
    lists.items = lists.items.filter((l) => l.listId !== list.listId);
    delete members[list.listId];
    const hit = limitReached(err);
    showError(hit ? t("limit_lists", { max: hit.max }) : t("list_save_failed"));
    return null;
  }
}

/**
 * Change a list. The one rule the screen has to agree with the server on: a list that becomes a
 * sharing one stops keeping its things out of All.
 *
 * @param {string} listId
 * @param {Partial<Pick<List, "name" | "sharing" | "date" | "keepOutOfAll">>} changes
 * @returns {Promise<boolean>} whether it was saved
 */
export async function updateList(listId, changes) {
  const before = lists.items;
  lists.items = lists.items.map((l) => {
    if (l.listId !== listId) return l;
    const next = { ...l, ...changes };
    return next.sharing ? { ...next, keepOutOfAll: false } : next;
  });
  try {
    const fetchopts = await getfetchopts({ json: changes });
    const res = await ky.put(`${LISTS_URL}/${listId}`, fetchopts).json();
    // A change answers with the list alone; its people are unchanged by it.
    lists.items = lists.items.map((l) => (l.listId === listId ? { ...listOnly(res.list), viewers: l.viewers } : l));
    return true;
  } catch (err) {
    console.error("Failed to update list", err);
    lists.items = before;
    showError(t("list_save_failed"));
    return false;
  }
}

/**
 * Remove a list. Its things stay — a list is only a tag over them.
 *
 * @param {string} listId
 */
export async function deleteList(listId) {
  const before = lists.items;
  const beforeMembers = members[listId];
  lists.items = lists.items.filter((l) => l.listId !== listId);
  delete members[listId];
  if (listView.selected === listId) listView.selected = "";
  try {
    const fetchopts = await getfetchopts();
    await ky.delete(`${LISTS_URL}/${listId}`, fetchopts);
  } catch (err) {
    console.error("Failed to delete list", err);
    lists.items = before;
    members[listId] = beforeMembers || [];
    showError(t("list_delete_failed"));
  }
}

/**
 * Put a thing on a list.
 *
 * @param {string} listId
 * @param {string} thingId
 * @param {{ send?: boolean }} [options] send: false for a capture, whose lists travel with it
 */
export async function fileOnList(listId, thingId, { send = true } = {}) {
  if (!send) unsent[listId] = [...(unsent[listId] || []), thingId];
  if (members[listId]?.includes(thingId)) return;
  members[listId] = [...(members[listId] || []), thingId];
  if (!send) return;
  try {
    const fetchopts = await getfetchopts();
    await ky.put(`${LISTS_URL}/${listId}/things/${thingId}`, fetchopts);
  } catch (err) {
    console.error("Failed to file thing", err);
    members[listId] = (members[listId] || []).filter((id) => id !== thingId);
    const hit = limitReached(err);
    showError(hit ? t("limit_list_full", { max: hit.max }) : t("list_file_failed"));
  }
}

/**
 * Take a thing off a list. The thing itself is untouched.
 *
 * @param {string} listId
 * @param {string} thingId
 */
export async function unfileFromList(listId, thingId) {
  if (!members[listId]?.includes(thingId)) return;
  members[listId] = members[listId].filter((id) => id !== thingId);
  if (unsent[listId]) unsent[listId] = unsent[listId].filter((id) => id !== thingId);
  try {
    const fetchopts = await getfetchopts();
    await ky.delete(`${LISTS_URL}/${listId}/things/${thingId}`, fetchopts);
  } catch (err) {
    console.error("Failed to unfile thing", err);
    members[listId] = [...(members[listId] || []), thingId];
    showError(t("list_file_failed"));
  }
}

/**
 * Put one of your followers on a sharing list: from now on they see what is on it. They are not
 * told (wip-archive/social/SOCIAL_PHASE2_SHARING.md, Q7).
 *
 * @param {string} listId
 * @param {import("$lib/people.svelte.js").Person} person
 */
export async function addViewer(listId, person) {
  const viewer = { userId: person.userId, firstName: person.firstName, handle: person.handle };
  const has = (/** @type {List} */ l) => l.viewers.some((v) => v.userId === person.userId);
  lists.items = lists.items.map((l) =>
    l.listId === listId && !has(l) ? { ...l, viewers: [...l.viewers, viewer] } : l
  );
  try {
    const fetchopts = await getfetchopts();
    await ky.put(`${LISTS_URL}/${listId}/viewers/${person.userId}`, fetchopts);
  } catch (err) {
    console.error("Failed to add a viewer", err);
    lists.items = lists.items.map((l) =>
      l.listId === listId ? { ...l, viewers: l.viewers.filter((v) => v.userId !== person.userId) } : l
    );
    const hit = limitReached(err);
    showError(hit ? t("limit_viewers", { max: hit.max }) : t("list_viewer_add_failed"));
  }
}

/**
 * Take someone off a list. They are not told; the list stops appearing for them (Q9).
 *
 * @param {string} listId
 * @param {string} userId
 */
export async function removeViewer(listId, userId) {
  const list = lists.items.find((l) => l.listId === listId);
  const viewer = list?.viewers.find((v) => v.userId === userId);
  if (!viewer) return;
  lists.items = lists.items.map((l) =>
    l.listId === listId ? { ...l, viewers: l.viewers.filter((v) => v.userId !== userId) } : l
  );
  try {
    const fetchopts = await getfetchopts();
    await ky.delete(`${LISTS_URL}/${listId}/viewers/${userId}`, fetchopts);
  } catch (err) {
    console.error("Failed to remove a viewer", err);
    lists.items = lists.items.map((l) => (l.listId === listId ? { ...l, viewers: [...l.viewers, viewer] } : l));
    showError(t("list_viewer_remove_failed"));
  }
}

/**
 * Someone removed as a follower has been taken off all your lists by the server; the same here,
 * so the list sheets do not show them until the next refresh.
 *
 * @param {string} userId
 */
export function forgetViewer(userId) {
  lists.items = lists.items.map((l) =>
    l.viewers.some((v) => v.userId === userId) ? { ...l, viewers: l.viewers.filter((v) => v.userId !== userId) } : l
  );
}

/**
 * The address of a list's link, for copying and sending: the app's own host, whichever
 * environment this is, and inside the iOS app too, where the page's host is not one.
 *
 * @param {string} token
 */
export const linkUrl = (token) => `https://${appHost()}/l/${token}/`;

/**
 * Turn a sharing list's link on or off (wip-archive/social/SOCIAL_PHASE3B_THE_LINK.md). Waits for
 * the server, unlike the other changes here: a link is something given to people outside the app,
 * and the screen must not offer one to send that does not work — nor say one is off while it still
 * opens.
 *
 * @param {string} listId
 * @param {boolean} on
 * @returns {Promise<{ ok: true, link: string | null } | { ok: false, reason: "offline" | "signed_out" | "failed" }>}
 */
export async function setListLink(listId, on) {
  try {
    // On, in this app's language: the email when someone asks to join from the link is in it.
    const fetchopts = await getfetchopts(on ? { json: { locale: getLocaleTag() } } : {});
    const url = `${LISTS_URL}/${listId}/link`;
    const res = /** @type {any} */ (await (on ? ky.put(url, fetchopts) : ky.delete(url, fetchopts)).json());
    const link = res.link ?? null;
    lists.items = lists.items.map((l) => (l.listId === listId ? { ...l, link } : l));
    return { ok: true, link };
  } catch (err) {
    console.error("Failed to change the list's link", err);
    return { ok: false, reason: failure(err) };
  }
}
